import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import useAskBeforeUnload from "_shared/_views/use-ask-before-unload";
import { useCallback, useRef } from "react";
import { type NavigateFunction, type NavigateOptions, type To, resolvePath, useLocation, useNavigate } from "react-router";
import useUnsavedProposalNavigationGuard from "work/proposals/_views/_shared/use-unsaved-proposal-navigation-guard";

/**
 * Asks before any navigation leaves a template editor that holds unsaved edits, and returns the
 * `navigate` the editor uses for its own moves.
 *
 * A template editor keeps its edits in local state, so every way off the page throws them away. Until
 * #7070 only Who has access's "Open full page" asked (review of #7023); a side-nav link, the browser's
 * Back, a reload or a closed tab left without a word. Now a link or Back asks here, and a reload,
 * a closed tab or a typed address has the browser ask.
 *
 * The editor's own moves do not ask. Its Back button asks in its own words first, and a save, delete
 * or archive moves the page only once its work is done — while the state that says the form is dirty
 * may not have caught up yet. So the editor navigates through the returned function, which lets that
 * one destination through.
 *
 * "Discard" goes on and "Cancel" stays with the edits. There is no "Save and leave": a template's save
 * reports a failure in a snackbar and resolves either way, so going on after it could still lose the
 * edits.
 */
export default function useConfirmLeavingUnsavedEdits(hasUnsavedEdits: boolean): NavigateFunction {
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const location = useLocation();
  // The destination the editor chose, or the reader chose after answering, which the guard lets through.
  const confirmedDestinationRef = useRef<string | null>(null);

  useUnsavedProposalNavigationGuard({
    shouldBlockNavigation: ({ currentLocation, nextLocation }) =>
      hasUnsavedEdits && nextLocation.pathname !== currentLocation.pathname,
    onNavigateAway: async (continueNavigation) => {
      const response = await confirm({
        title: "Discard changes?",
        message: "Leaving this page discards the changes you have not saved.",
        okButtonText: "Discard",
        cancelButtonText: "Cancel",
      });
      if (response !== ConfirmResponse.Ok) return false;
      continueNavigation();
      return true;
    },
    appDirectedNavigationPathRef: confirmedDestinationRef,
  });
  useAskBeforeUnload(hasUnsavedEdits);

  const currentPathname = location.pathname;
  // Stable on purpose, not defensive memoization: the editors list `navigate` in effect dependencies,
  // as they did `useNavigate`'s, and a new function each render would re-run those effects.
  const navigateWithoutAsking = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === "number") return navigate(to);
      confirmedDestinationRef.current = resolvePath(to, currentPathname).pathname;
      return navigate(to, options);
    },
    [currentPathname, navigate],
  );
  return navigateWithoutAsking as NavigateFunction;
}
